import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LoadScreen } from "@/core/components/shared";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSettlementConfig } from "@/features/merchant/api/use-settlement-config";
import { useUpdateSettlementConfig } from "@/features/merchant/api/use-update-settlement-config";
import type { MerchantSettlementConfigDto } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createSettlementConfigSchema,
  type SettlementConfigFormValues,
} from "@/features/merchant/schemas/settlement-config.schema";
import {
  PAYOUT_METHODS,
  SETTLEMENT_FREQUENCIES,
} from "@/features/shared/api/enums";

const emptyFormValues = (merchantId: string): SettlementConfigFormValues => ({
  merchantId,
  frequency: "INSTANT",
  payoutMethod: "FLASH_WALLET",
  bankAccount: "",
});

/**
 * Section « Reversement » d'un marchand — affiche la config actuelle et
 * permet de la mettre à jour (rôle merchant avec permission
 * `MANAGE_SETTLEMENT_CONFIG` ou back-office).
 */
export function SettlementConfigSection({
  merchantId,
  actingMerchantId,
}: {
  merchantId: string;
  actingMerchantId?: string;
}) {
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue.
  const schema = useMemo(() => createSettlementConfigSchema(), [i18n.language]);

  const { data, isLoading, error, refetch } = useSettlementConfig(merchantId);
  const update = useUpdateSettlementConfig(merchantId);

  const initial = useMemo<SettlementConfigFormValues>(
    () => ({
      merchantId,
      frequency: data?.frequency ?? "INSTANT",
      payoutMethod: data?.payoutMethod ?? "FLASH_WALLET",
      bankAccount: data?.bankAccount ?? "",
    }),
    [merchantId, data]
  );

  const form = useForm({
    defaultValues: emptyFormValues(merchantId),
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: MerchantSettlementConfigDto = {
        merchantId,
        frequency: parsed.data.frequency,
        payoutMethod: parsed.data.payoutMethod,
        bankAccount:
          parsed.data.payoutMethod === "BANK_ACCOUNT"
            ? parsed.data.bankAccount?.trim() || undefined
            : undefined,
      };
      update.mutate(
        { body: payload, actingMerchantId },
        {
          onSuccess: () =>
            notifSilence(tm("merchant.settlement.feedback.updated")),
          onError: (error) => notifSilenceError(error.message),
        }
      );
    },
  });

  useEffect(() => {
    if (data) form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadScreen />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {tm("merchant.settlement.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && !data ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-destructive">{error.message}</p>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {tm("merchant.settlement.empty")}
            </Button>
          </div>
        ) : null}

        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="frequency">
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tm("merchant.settlement.frequency")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(
                      v as SettlementConfigFormValues["frequency"]
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SETTLEMENT_FREQUENCIES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="payoutMethod">
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tm("merchant.settlement.payoutMethod")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(
                      v as SettlementConfigFormValues["payoutMethod"]
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYOUT_METHODS.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Subscribe selector={(state) => state.values.payoutMethod}>
            {(method) =>
              method === "BANK_ACCOUNT" ? (
                <form.Field name="bankAccount">
                  {(field) => (
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="settlement-bank-account">
                        {tm("merchant.settlement.bankAccount")}
                      </Label>
                      <Input
                        id="settlement-bank-account"
                        className="notranslate"
                        value={field.state.value ?? ""}
                        placeholder={tm(
                          "merchant.settlement.bankAccountPlaceholder"
                        )}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    </div>
                  )}
                </form.Field>
              ) : null
            }
          </form.Subscribe>

          {data?.nextSettlementAt ? (
            <div>
              <p className="text-xs text-muted-foreground">
                {tm("merchant.settlement.nextSettlementAt")}
              </p>
              <NoTranslate as="div" className="font-medium">
                {formatDateWithFallback(data.nextSettlementAt, "—")}
              </NoTranslate>
            </div>
          ) : null}
          {data?.updatedAt ? (
            <div>
              <p className="text-xs text-muted-foreground">
                {tm("merchant.settlement.updatedAt")}
              </p>
              <NoTranslate as="div" className="font-medium">
                {formatDateWithFallback(data.updatedAt, "—")}
              </NoTranslate>
            </div>
          ) : null}

          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <ButtonSpinner /> : null}
              {tm("merchant.settlement.submit")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
